import { useState, useCallback, useRef } from "react";
import { Bluelagoon } from "@bluelagoon";
import { useIntegrationBuildTask } from "./useIntegrationBuildTask";
import { useEndpointTesting } from "./useEndpointTesting";
import { useAgentChat } from "./useAgentChat";

export function useIntegrationBuilder({ editingIntegration } = {}) {
    const [url, setUrl] = useState("");
    const [credentials, setCredentials] = useState({});

    const buildTask = useIntegrationBuildTask({ editingIntegration });

    const fullIntegration = buildTask.integration || editingIntegration;

    const onIntegrationUpdated = useCallback((updated) => {
        if (updated?.endpoints) buildTask.setEndpointsFromAgent(updated.endpoints);
        if (updated?.auth) buildTask.setAuthFromAgent(updated.auth);
        if (updated?.baseUrl) buildTask.setApiInfoFromAgent(updated);
    }, [buildTask]);

    const testing = useEndpointTesting({
        endpoints: buildTask.endpoints,
        apiType: buildTask.apiType,
        auth: buildTask.auth,
        credentials,
        addChat: buildTask.addChat,
        setPhase: buildTask.setPhase,
        baseUrl: fullIntegration?.baseUrl || "",
        integration: fullIntegration,
    });

    const agentChat = useAgentChat({
        addChat: buildTask.addChat,
        integration: fullIntegration,
        auth: buildTask.auth,
        credentials,
        endpoints: buildTask.endpoints,
        onIntegrationUpdated,
    });

    const submitUrl = (visibility) => buildTask.submitUrl(url, visibility);
    const skipToComplete = () => buildTask.setPhase("complete");

    // Auto-connect on first start of testing — guarantees the integration lands in
    // My Connections without an extra step. Idempotent: silently no-ops when
    // already connected, and never blocks testing if it fails.
    const autoConnectAttemptedRef = useRef(false);
    const autoConnect = useCallback(async () => {
        if (autoConnectAttemptedRef.current) return;
        const communityIntegrationId = buildTask.draftId || editingIntegration?.id;
        if (!communityIntegrationId) return;
        const authType = buildTask.auth?.type || "none";
        const needsCredentials = authType !== "none";
        if (needsCredentials && (!credentials || Object.keys(credentials).length === 0)) return;
        autoConnectAttemptedRef.current = true;
        try {
            await Bluelagoon.connectCommunityIntegration({ communityIntegrationId, credentials: credentials || {} });
            buildTask.addChat({ role: "ai", text: "✓ Connected to your workspace — ready to use." });
        } catch (err) {
            const errData = err?.response?.data?.data || err?.response?.data;
            if (errData?.error === "ALREADY_CONNECTED") return;
            // Don't block testing — just surface a hint
            buildTask.addChat({ role: "ai", text: `Note: auto-connect skipped (${errData?.message || err.message}). You can connect manually after publishing.` });
        }
    }, [buildTask, credentials, editingIntegration]);

    const startTestingWithConnect = useCallback(() => {
        autoConnect();
        testing.startTesting();
    }, [autoConnect, testing]);

    return {
        phase: buildTask.phase, apiType: buildTask.apiType, draftId: buildTask.draftId, taskId: buildTask.taskId,
        url, setUrl, submitUrl,
        chatMessages: buildTask.chatMessages,
        endpoints: buildTask.endpoints, revealedEndpoints: buildTask.revealedEndpoints,
        credentials, setCredentials,
        errorMap: buildTask.errorMap, revealedErrors: buildTask.revealedErrors,
        integration: fullIntegration,
        auth: buildTask.auth, error: buildTask.error,
        currentTestEndpoint: testing.currentTestEndpoint,
        currentTestResult: testing.currentTestResult,
        currentFix: testing.currentFix,
        approvedIds: testing.approvedIds,
        startTesting: startTestingWithConnect,
        approveEndpoint: testing.approveEndpoint,
        skipEndpoint: testing.skipEndpoint,
        autoApprove: testing.autoApprove,
        toggleAutoApprove: testing.toggleAutoApprove,
        pendingParams: testing.pendingParams,
        submitParams: testing.submitParams,
        retestEndpoint: testing.retestEndpoint,
        fixWithAI: testing.fixWithAI,
        isFixing: testing.isFixing,
        sendMessage: agentChat.sendMessage,
        isSending: agentChat.isSending,
        setApiInfo: buildTask.setApiInfoFromAgent,
        reset: buildTask.reset,
        skipToComplete,
        taskStartedAt: buildTask.taskStartedAt,
        isConnected: buildTask.isConnected,
        isPolling: buildTask.isPolling,
        connectionFailed: buildTask.connectionFailed,
        isRecovering: buildTask.isRecovering,
    };
}
